import { ClassificationType } from "@/types/config";
import { Document, SentimentType, Suggestion } from "@/types/document";

export const isInlineSuggestion = (suggestion: Suggestion) => {
  return (
    !suggestion.rating ||
    (suggestion.rating === 3 && suggestion.blocks.length <= 2)
  );
};

/**
 * Resolves a suggestion for a given block ID in a document.
 * First looks for a suggestion where blocks.length <= 5 and first id matches block id.
 * Otherwise checks for suggestion where blocks.length == 1 and block id matches.
 *
 * @param document The document containing suggestions and blocks
 * @param blockId The ID of the block to find suggestions for
 * @param classificationFilters Active classification filters - empty array means show none
 * @returns The matching suggestion or undefined if none found
 */
export const resolveSuggestion = (
  document: Document,
  blockId: string,
  classificationFilters: ClassificationType[] = []
): Suggestion | undefined => {
  if (!document.suggestions) {
    return undefined;
  }

  const isVisible = (suggestion: Suggestion) => {
    if (!suggestion.classification) {
      return true;
    }

    // If no filters active, show no suggestions
    if (classificationFilters.length === 0) return false;

    // Otherwise only show if classification matches an active filter
    return (
      suggestion.classification &&
      classificationFilters.includes(
        suggestion.classification as ClassificationType
      )
    );
  };

  // First try to find a suggestion with <= 3 blocks where the first block matches
  const multiBlockSuggestion = document.suggestions.find(
    (suggestion) =>
      isInlineSuggestion(suggestion) &&
      suggestion.primaryBlockId === blockId &&
      suggestion.status !== "rejected" &&
      isVisible(suggestion)
  );

  if (multiBlockSuggestion) {
    return multiBlockSuggestion;
  }

  // Otherwise look for single block suggestion matching the block ID
  return document.suggestions.find(
    (suggestion) =>
      suggestion.blocks.length === 1 &&
      suggestion.blocks[0] === blockId &&
      suggestion.status !== "rejected" &&
      isVisible(suggestion)
  );
};

export const blockLabel = (document: Document, blockId: string) => {
  const group = document.groups.find((group) =>
    group.blockIds.includes(blockId)
  );

  if (!group) {
    throw new Error(`Block ${blockId} not found in any group`);
  }

  const index = group.blockIds.indexOf(blockId);
  return `B${index + 1}`;
};

export const findBlockIndexInGroups = (
  document: Document,
  blockId: string
): number => {
  for (const group of document.groups) {
    const index = group.blockIds.indexOf(blockId);
    if (index !== -1) {
      return index;
    }
  }
  return -1;
};

export const resolveSentimentBackgroundColor = (
  sentiment: SentimentType | null | undefined
) => {
  if (!sentiment) return "";
  switch (sentiment) {
    case "positive":
      return "bg-positive";
    case "negative":
      return "bg-negative";
    case "neutral":
      return "bg-neutral";
    default:
      return "";
  }
};

export const resolveSentimentSecondaryBorderColor = (
  sentiment: SentimentType | null | undefined,
  classification?: string | null | undefined
) => {
  const color = resolveSentimentBorderColor(sentiment, classification);
  if (color) {
    return color + "-secondary";
  }

  return color;
};

export const resolveSentimentBorderColor = (
  sentiment: SentimentType | null | undefined,
  classification?: string | null | undefined
) => {
  if (!sentiment) return "";

  if (classification) {
    return "border-classification";
  }

  switch (sentiment) {
    case "positive":
      return "border-positive";
    case "negative":
      return "border-negative";
    case "neutral":
      return "border-neutral";
    default:
      return "";
  }
};

export const resolveSentimentMarkerColor = (
  sentiment: SentimentType | null | undefined,
  classification?: string | null | undefined
) => {
  if (!sentiment) return "";

  if (classification) {
    return "marker-classification";
  }

  switch (sentiment) {
    case "positive":
      return "marker-positive";
    case "negative":
      return "marker-negative";
    default:
      return "";
  }
};
